import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

// Lodash
import { cloneDeep } from 'lodash-es';

// Material
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

// Services
import { RecordsService } from '@modules/records/services/records.service';

// Pipes
import { DifferencePipe } from '@modules/elements/pipes/difference.pipe';

// Types
import { Record } from '@modules/records/types/record';
import { RecordOptions } from '@modules/records/types/record-options';
import { UserPermissions } from '@modules/users/types/user';
import { Project } from '@modules/project/types/project';

@Component({
  selector: 'app-record-info',
  templateUrl: './record-info.component.html',
  styleUrl: './record-info.component.less',
  standalone: false
})
export class RecordInfoComponent implements OnChanges {

  // Inputs
  @Input() record?: Record;
  @Input() permissions?: UserPermissions[];
  @Input() project?: Project;

  // Public
  public originRecord?: Record;
  public startTime?: Date;
  public recordMetadata: {
    vod: {key: string, value: string}[],
    max: {key: string, value: string}[],
    linear: {key: string, value: string}[],
    archive: {key: string, value: string}[],
    c3: {key: string, value: string}[],
    hudsonYards: {key: string, value: string}[],
    additionalMetadata: {key: string, value: string}[],
    contentAcquisition: {key: string, value: string}[],
    s3Download: {key: string, value: string}[]
  } = { vod: [], max: [], linear: [], archive: [], hudsonYards: [], c3: [], additionalMetadata: [], contentAcquisition: [], s3Download: [] };
  public recordOptions = new RecordOptions();
  public minDate = new Date();
  public durationMinutes = 0;
  public disabled = false;
  public editing = false;
  public timezone: string = 'America/New_York';
  public metadataTasks = {
    vod: false,
    max: false,
    linear: false,
    archive: false,
    c3: false,
    contentAcquisition: false,
    s3Download: false
  };

  /**
   * Constructor
   */

  constructor(
    private recordsService: RecordsService,
    private differencePipe: DifferencePipe,
  ) { }

  /**
   * Component lifecycle
   */

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['record'] && this.record) {
      this.originRecord = cloneDeep(this.record);
      this.startTime = new Date(this.record.startTime || 0);
      if (this.record.durationType === 'seconds') {
        this.durationMinutes = +(this.record.durationValue || 0) / 60;
      }
      this.setupMetadataTasks();
      this.setupMetadata();
      this.parseMetadata();
      this.recalcDisabled();
      this.updateRecord();
    }
    if (changes['permissions']) {
      this.recalcDisabled();
    }
    if (changes['project']) {
      this.setupMetadataTasks();
    }
  }

  /**
   * Methods
   */

  setupMetadataTasks(): void {
    if (this.record?.servers?.length && this.record?.servers.every(server => server.tasks.length > 0)) {
      this.metadataTasks.vod = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('vod'))) || false;
      this.metadataTasks.max = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('max'))) || false;
      this.metadataTasks.linear = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('linear'))) || false;
      this.metadataTasks.archive = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('archive'))) || false;
      this.metadataTasks.c3 = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('c3'))) || false;
      this.metadataTasks.contentAcquisition = this.record?.servers?.some(server => server.tasks?.some(task => task.type?.includes('contentAcquisition'))) || false;
      this.metadataTasks.s3Download = this.record?.servers?.some(server => server.tasks?.some(task => task.type ==='s3downloadVideo' && task.body?.autoStartS3Download)) || false;
    } else {
      this.metadataTasks.vod = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('vod'))) || false;
      this.metadataTasks.max = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('max'))) || false;
      this.metadataTasks.linear = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('linear'))) || false;
      this.metadataTasks.archive = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('archive'))) || false;
      this.metadataTasks.c3 = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('c3'))) || false;
      this.metadataTasks.contentAcquisition = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type?.includes('contentAcquisition'))) || false;
      this.metadataTasks.s3Download = this.project?.serverTemplates?.some(server => server.taskTemplates?.some(task => task.type ==='s3downloadVideo' && task.body?.autoStartS3Download)) || false;
    }
  }

  setupMetadata(): void {
    this.recordMetadata.vod = this.recordOptions.metadataVodDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.max = this.recordOptions.metadataMaxDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.linear = this.recordOptions.metadataLinearDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.archive = this.recordOptions.metadataArchiveDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.c3 = this.recordOptions.metadataC3DefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.contentAcquisition = this.recordOptions.metadataContentAcquisitionDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.hudsonYards = this.recordOptions.metadataHudsonYardsDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.s3Download = this.recordOptions.metadataS3DownloadDefaultKeys.map(key => ({key, value: ''}));
    this.recordMetadata.additionalMetadata = this.recordOptions.metadataGeneralDefaultKeys.map(key => ({key, value: ''}));
    if (this.record?.servers && this.record?.servers?.length > 1) {
      this.recordMetadata.c3.push(...this.recordOptions.metadataC3BackupDefaultKeys.map(key => ({key, value: ''})));
    }
  }

  parseMetadata(): void {
    for (const key in this.record?.additionalMetadata) {
      let index = this.recordMetadata.vod.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.vod[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.max.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.max[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.linear.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.linear[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.archive.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.archive[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.c3.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.c3[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.contentAcquisition.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.contentAcquisition[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.s3Download.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.s3Download[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.hudsonYards.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.hudsonYards[index].value = this.record.additionalMetadata[key];
        continue;
      }
      index = this.recordMetadata.additionalMetadata.findIndex(item => item.key === key);
      if (index !== -1) {
        this.recordMetadata.additionalMetadata[index].value = this.record.additionalMetadata[key];
        continue;
      }
      this.recordMetadata.additionalMetadata.push({key, value: this.record.additionalMetadata[key]});
    }
  }

  getMetadata(): {[key: string]: string} {
    const metadata: {[key: string]: string} = {}
    this.recordMetadata.vod.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.max.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.linear.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.c3.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.contentAcquisition.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.s3Download.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.archive.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.hudsonYards.forEach(item => metadata[item.key] = item.value);
    this.recordMetadata.additionalMetadata.forEach(item => metadata[item.key] = item.value);
    return metadata;
  }

  updateDuration(option?: any): void {
    if (option && this.record) {
      this.record.durationType = option.value;
      this.updateRecord();
    }
  }

  addAdditionalMetadata(): void {
    let key = 'Key';
    let index = 1;
    while (this.recordMetadata.additionalMetadata.find(item => item.key === key)) {
      index++;
      key = `Key ${index}`;
    }
    this.recordMetadata.additionalMetadata.push({key: key, value: ''});
    this.updateAdditionalMetadata();
  }

  deleteAdditionalMetadata(index: number): void {
    this.recordMetadata.additionalMetadata.splice(index, 1);
    this.updateAdditionalMetadata();
  }

  updateAdditionalMetadata(): void {
    if (this.record) {
      this.record.additionalMetadata = this.getMetadata();
      this.updateRecord();
    }
  }

  updateDate(event: MatDatepickerInputEvent<Date>): void {
    if (event?.value && this.record) {
      const time = this.startTime ? new Date(this.startTime) : new Date();
      this.startTime = event.value;
      this.startTime.setSeconds(0);
      this.startTime.setMinutes(time.getMinutes());
      this.startTime.setHours(time.getHours());
    }
    this.updateRecord();
  }

  updateTimezone(option?: any): void {
    if (option && this.timezone) {
      this.timezone = option.value;
    }
  }

  updateRecord(): void {
    if (this.record) {
      if (this.record.durationType === 'seconds') {
        this.record.durationValue = (this.durationMinutes * 60) + '';
      }
      this.applyStartTime();
      const recordDiff: Partial<Record> = this.differencePipe.transform(this.record, this.originRecord);
      this.editing = Object.keys(recordDiff).length > 0;
    }
  }

  getDisplayKey(key: string): string {
    return this.recordOptions.overrideMetadataKeys[key] || key;
  }

  /**
   * Actions
   */

  saveRecord(): void {
    if (this.record) {
      if (this.record.durationType === 'seconds') {
        this.record.durationValue = (this.durationMinutes * 60) + '';
      }
      this.applyStartTime();
      this.recordsService.updateRecord(this.getRecordPayload(this.record));
    }
  }

  resetRecordChanges(): void {
    if (this.originRecord) {
      this.recordsService.updateRecord(this.getRecordPayload(this.originRecord));
    }
  }

  /**
   * Helpers
   */

  /**
   * Only a scheduled record's start time belongs to the client. Once the record is preparing,
   * running or done the server owns startTime — we neither rewrite it locally nor send it back.
   */
  get isStartTimeLocked(): boolean {
    return this.record?.status !== 'scheduled';
  }

  private applyStartTime(): void {
    if (!this.record || this.isStartTimeLocked) { return; }
    this.startTime?.setSeconds(0);
    this.record.startTime = this.startTime?.toISOString();
  }

  /**
   * A started record's startTime is dropped from the PATCH body entirely, so the server is never
   * asked to move it.
   */
  private getRecordPayload(record: Record): Record {
    const payload = cloneDeep(record);
    if (this.isStartTimeLocked) {
      delete payload.startTime;
    }
    return payload;
  }

  private recalcDisabled(): void {
    this.disabled = this.record?.status !== 'scheduled' || (!this.permissions?.includes('record:update'));
  }

}
